/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { BadgeCheck, Layers, Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api';
import type { AccountType, CustomerStatus, CustomerSummary, CustomerTier } from '../../api/areas/accounts';
import { DataError } from '../../components/DataError';
import { DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { BulkList, useSelection, type BulkAction } from '../../components/BulkList';
import { SavedViews } from '../../components/SavedViews';
import { useAccess } from '../../lib/access';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { NewCustomerDialog } from './NewCustomerDialog';
import {
  cellLink,
  CUSTOMER_STATUS,
  EnumBadge,
  EnumText,
  FilterSelect,
  FilterText,
  Masked,
  mutedSmall,
  Pager,
  paths,
  RefreshButton,
  ResetFilters,
  TierText,
  TypeCountry,
  useEnumLabel,
  useListParams,
  useTierLabel,
} from './shared';

const LIMIT = 50;
const ACCOUNT_TYPES: readonly AccountType[] = ['INDIVIDUAL', 'ORGANIZATION'];
const STATUSES: readonly CustomerStatus[] = ['ACTIVE', 'SUSPENDED', 'CLOSED'];
const FILTERS = ['q', 'status', 'account_type'] as const;

/**
 * Changes one field of the account the way its edit dialog does (every other field as stored, the
 * billing profile untouched), against the version just read. Unchanged: nothing is sent.
 */
async function editAccount(id: string, field: 'status' | 'tier', value: string, reason: string, idempotencyKey: string): Promise<boolean> {
  const a = await api.getCustomerAccount(id);
  if (a[field] === value) return false;
  await api.updateCustomer(
    id,
    {
      email: a.email,
      phone: a.phone,
      billing_currency: a.billing_currency,
      tier: a.tier,
      status: a.status,
      [field]: value,
      expected_version: a.version,
      reason,
    },
    { idempotencyKey },
  );
  return true;
}

/** `/customers`: commercial accounts (not registrants), newest first. */
export function CustomersPage() {
  const { t } = useTranslation('accounts');
  const label = useEnumLabel('accounts');
  useDocumentTitle(t('customers.title'));
  const id = useId();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const list = useListParams(FILTERS);
  const { q, status, account_type } = list.values;
  const result = useApi(
    (signal) =>
      api.listCustomers(
        {
          q: q || undefined,
          status: (status || undefined) as CustomerStatus | undefined,
          account_type: (account_type || undefined) as AccountType | undefined,
          after: list.after || undefined,
          limit: LIMIT,
        },
        { signal },
      ),
    [q, status, account_type, list.after],
  );
  const { t: tu } = useTranslation('ux');
  const access = useAccess();
  const manage = !access.client && access.has(['customer.manage']);
  const tiersApi = useApi((signal) => (manage ? api.listCustomerTiers({ signal }) : Promise.resolve({ items: [] as CustomerTier[] })), [manage]);
  const tiers = tiersApi.status === 'success' ? tiersApi.data.items : [];
  const tierLabel = useTierLabel();
  const tierName = (code: string) => {
    const tier = tiers.find((x) => x.code === code);
    return tier ? tierLabel(tier) : code;
  };
  const selection = useSelection<CustomerSummary>((c) => c.id);
  const pageRows = result.status === 'success' ? result.data.items : [];
  const changed = (did: boolean) => (did ? tu('lists.changed') : tu('lists.unchanged'));
  const actions: BulkAction<CustomerSummary>[] = manage
    ? [
        {
          id: 'status',
          label: tu('lists.customers.status'),
          icon: BadgeCheck,
          choice: { label: tu('lists.customers.newStatus'), options: STATUSES.map((x) => ({ value: x, label: label('customerStatus', x) })) },
          reason: true,
          skip: (c, i) => (c.status === i.choice ? tu('lists.unchanged') : null),
          run: async (c, i, key) => changed(await editAccount(c.id, 'status', i.choice, i.reason, key)),
        },
        {
          id: 'tier',
          label: tu('lists.customers.tier'),
          icon: Layers,
          choice: { label: tu('lists.customers.newTier'), options: tiers.map((x) => ({ value: x.code, label: tierLabel(x) })) },
          reason: true,
          run: async (c, i, key) => changed(await editAccount(c.id, 'tier', i.choice, i.reason, key)),
        },
      ]
    : [];

  const columns: Column<CustomerSummary>[] = [
    ...(access.client ? [] : [selection.column(pageRows, (c) => tu('bulkList.select', { name: c.display_name }), tu('bulkList.selectAll'))]),
    {
      key: 'customer',
      header: t('customers.col.customer'),
      cell: (c) => (
        <div className="min-w-44 space-y-0.5">
          <Link to={paths.customer(c.id)} className={cellLink}>
            {c.display_name}
          </Link>
          <div className={mutedSmall}>
            <Masked value={c.email_hint} />
          </div>
        </div>
      ),
    },
    { key: 'type', header: t('customers.col.typeCountry'), cell: (c) => <TypeCountry type={<EnumText group="accountType" value={c.account_type} />} country={c.country_code} /> },
    { key: 'status', header: t('customers.col.status'), cell: (c) => <EnumBadge group="customerStatus" tones={CUSTOMER_STATUS} value={c.status} /> },
    { key: 'tier', header: t('common.tier'), cell: (c) => <TierText code={c.tier} tiers={tiers} /> },
    { key: 'domains', header: t('customers.col.domains'), numeric: true, cell: (c) => formatCount(c.domain_count) },
    { key: 'subjects', header: t('customers.col.subjects'), numeric: true, cell: (c) => formatCount(c.subject_count) },
    { key: 'currency', header: t('customers.col.currency'), cell: (c) => <span className="font-mono text-xs">{c.billing_currency}</span> },
    { key: 'created', header: t('customers.col.created'), className: 'whitespace-nowrap', cell: (c) => formatDate(c.created_at) },
  ];

  return (
    <>
      <PageHeader title={t('customers.title')} crumbs={[{ label: t('crumb') }]} subtitle={t('customers.subtitle')} actions={
          <>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden="true" /> {t('newCustomer.button')}
            </Button>
            <RefreshButton onClick={result.reload} />
          </>
        }
      />
      <NewCustomerDialog open={creating} onOpenChange={setCreating} onCreated={(cid) => void navigate(paths.customer(cid))} />
      <FilterBar actions={<ResetFilters show={list.filtered} onReset={list.reset} />}>
        <FilterText id={`${id}-q`} label={t('filter.search')} value={q} placeholder={t('customers.searchPlaceholder')} onApply={(v) => list.setFilter('q', v)} />
        <FilterSelect
          id={`${id}-s`}
          label={t('filter.status')}
          value={status}
          options={Object.keys(CUSTOMER_STATUS).map((s) => [s, label('customerStatus', s)] as const)}
          onChange={(v) => list.setFilter('status', v)}
        />
        <FilterSelect id={`${id}-t`} label={t('filter.type')} value={account_type} options={ACCOUNT_TYPES.map((s) => [s, label('accountType', s)] as const)} onChange={(v) => list.setFilter('account_type', v)} />
      </FilterBar>
      <SavedViews list="customers" keys={FILTERS} reset={['after']} />
      <BulkList
        selection={selection}
        actions={actions}
        rowKey={(c) => c.id}
        rowLabel={(c) => c.display_name}
        onDone={result.reload}
        csv={{
          name: 'customers',
          columns: [
            { header: t('customers.col.customer'), value: (c) => c.display_name },
            { header: t('customers.col.typeCountry'), value: (c) => `${label('accountType', c.account_type)} · ${c.country_code ?? ''}` },
            { header: t('customers.col.status'), value: (c) => label('customerStatus', c.status) },
            { header: t('common.tier'), value: (c) => tierName(c.tier) },
            { header: t('customers.col.domains'), value: (c) => c.domain_count },
            { header: t('customers.col.subjects'), value: (c) => c.subject_count },
            { header: t('customers.col.currency'), value: (c) => c.billing_currency },
            { header: t('customers.col.created'), value: (c) => c.created_at },
          ],
        }}
      />
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('customers.loadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={result.data.items} rowKey={(c) => c.id} empty={<EmptyState title={t('customers.empty')} />} />
            {result.data.items.length > 0 ? <Pager shown={result.data.items.length} hasPrev={Boolean(list.after)} nextAfter={result.data.next_after} onPrev={list.prev} onNext={list.next} /> : null}
          </>
        ) : null}
      </Section>
    </>
  );
}
